import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Building2 } from 'lucide-react';
import { Button, Card, Chip, FormField, Input, Select } from '@/ui';
import { maskEmail, maskPhone, revealOrMask } from '@/lib/admin-mask';
import {
  EMPTY_ORGANIZATION_FILTERS,
  fetchPlatformOrganizations,
  type OrganizationListFilters,
} from './api';
import { adminKeys, filtersKey } from './query-keys';
import { formatCount, formatDate } from './format';
import { ListStatus, LoadMore } from './ListStatus';
import { flattenPages, usePagedQuery } from './use-paged-query';
import { CreateOrganizationDrawer } from './CreateOrganizationDrawer';
import styles from './Admin.module.css';

/**
 * PLATFORM ISLETMELERI (`/yonetim/isletmeler`).
 *
 * Kullanici listesiyle AYNI iki karar gecerli: filtreler adres cubuguna
 * YAZILMAZ ve iletisim bilgileri varsayilan olarak MASKELI. Isletme e-postasi
 * kisisel veri sayilmasa da ayni ekranda iki farkli maskeleme kurali olmasi
 * kullaniciya "hangisi acik, hangisi kapali" sorusu sordururdu.
 *
 * DEGISTIREN eylem burada YOK: isletme pasiflestirme, ETKIN HER UYENIN
 * jetonlarini iptal ediyor. Bu buyukluktekt bir islem, kimi etkiledigini
 * gosteren detay sayfasina ait.
 */

const ACTIVE_VALUES = { active: 'true', inactive: 'false' } as const;

const toIsActive = (value: string): boolean | null => {
  if (value === ACTIVE_VALUES.active) return true;
  if (value === ACTIVE_VALUES.inactive) return false;
  return null;
};

export function PlatformOrganizationsPage() {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<OrganizationListFilters>(EMPTY_ORGANIZATION_FILTERS);
  const [applied, setApplied] = useState<OrganizationListFilters>(EMPTY_ORGANIZATION_FILTERS);
  const [revealContact, setRevealContact] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const key = filtersKey({ q: applied.q.trim(), isActive: applied.isActive });

  const query = usePagedQuery(adminKeys.organizationList(key), (pageParam) =>
    fetchPlatformOrganizations({ filters: applied, ...pageParam }),
  );

  const rows = flattenPages(query.data);
  const filtered = applied.q.trim() !== '' || applied.isActive !== null;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setApplied(draft);
  };

  const clear = (): void => {
    setDraft(EMPTY_ORGANIZATION_FILTERS);
    setApplied(EMPTY_ORGANIZATION_FILTERS);
  };

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>{t('admin.organizations.title')}</h1>
            <p className={styles.subtitle}>
              {t('admin.organizations.loadedCount', { n: rows.length })}
            </p>
          </div>
          {/* Self-servis kayit kapali (2026-08-30): isletme yaratmanin TEK yolu bu dugme. */}
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            {t('admin.organizations.createAction')}
          </Button>
        </div>
      </Card>

      <Card padding="md">
        <form className={styles.filterRow} onSubmit={submit}>
          <FormField
            label={t('admin.organizations.searchLabel')}
            className={styles.filterField}
          >
            <Input
              value={draft.q}
              placeholder={t('filter.search')}
              autoComplete="off"
              onChange={(event) => {
                setDraft((current) => ({ ...current, q: event.target.value }));
              }}
            />
          </FormField>

          <FormField label={t('admin.users.statusLabel')} className={styles.filterField}>
            <Select
              value={
                draft.isActive === null
                  ? ''
                  : draft.isActive
                    ? ACTIVE_VALUES.active
                    : ACTIVE_VALUES.inactive
              }
              placeholder={t('admin.filters.all')}
              options={[
                { value: ACTIVE_VALUES.active, label: t('admin.status.active') },
                { value: ACTIVE_VALUES.inactive, label: t('admin.status.inactive') },
              ]}
              onChange={(event) => {
                setDraft((current) => ({ ...current, isActive: toIsActive(event.target.value) }));
              }}
            />
          </FormField>

          <div className={styles.filterActions}>
            <Button type="submit">{t('filter.apply')}</Button>
            <Button type="button" tone="ghost" onClick={clear}>
              {t('filter.clearAll')}
            </Button>
          </div>
        </form>

        <label className={styles.revealToggle}>
          <input
            type="checkbox"
            checked={revealContact}
            onChange={(event) => {
              setRevealContact(event.target.checked);
            }}
          />
          {t('admin.revealContact')}
        </label>
      </Card>

      <ListStatus
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        isEmpty={query.isSuccess && rows.length === 0}
        emptyIcon={<Building2 size={28} aria-hidden="true" />}
        emptyTitle={filtered ? t('list.noResults.title') : t('list.empty.title')}
        {...(filtered ? { emptyDescription: t('list.noResults.description') } : {})}
        onRetry={() => {
          void query.refetch();
        }}
      />

      {rows.length > 0 ? (
        <Card padding="lg">
          <div className={styles.rowList}>
            {rows.map((row) => (
              <div key={row.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowName}>{row.organizationName}</span>
                  <span className={styles.muted}>
                    {revealOrMask(row.organizationEmail, revealContact, maskEmail)} ·{' '}
                    {revealOrMask(row.organizationPhone, revealContact, maskPhone)}
                  </span>
                  <span className={styles.muted}>
                    {row.organizationCity}
                    {row.organizationDistrict === '' ? '' : ` / ${row.organizationDistrict}`} ·{' '}
                    {t('admin.organizations.createdAt', { date: formatDate(row.createdAt) })}
                  </span>
                </div>
                <div className={styles.badges}>
                  <Chip tone="neutral">
                    {t('admin.organizations.memberCount', {
                      active: formatCount(row.activeMemberCount),
                      total: formatCount(row.memberCount),
                    })}
                  </Chip>
                  <Chip tone={row.isActive ? 'success' : 'danger'}>
                    {row.isActive ? t('admin.status.active') : t('admin.status.inactive')}
                  </Chip>
                  <Link to={`/yonetim/isletmeler/${row.id}`} className={styles.linkButton}>
                    {t('admin.organizations.openDetail')}
                  </Link>
                  {/* IBAN'lar platform yonetiminde ve tanimlama ekrani detay sayfasinin
                      icinde; kisayol olmadan "hesap eklemek icin nereye gidilir" gorunmez
                      kaliyordu. (Metrikler sayfasindaki `#duyuru` ile ayni desen.) */}
                  <Link
                    to={`/yonetim/isletmeler/${row.id}#hesaplar`}
                    className={styles.linkButton}
                  >
                    {t('admin.accounts.sectionTitle')}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <LoadMore
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            onLoadMore={() => {
              void query.fetchNextPage();
            }}
          />
        </Card>
      ) : null}

      <CreateOrganizationDrawer
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
      />
    </div>
  );
}
